import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { DropdownField } from 'ui-components/components/dropdown/dropdown-field';
import { WeightInput } from 'ui-components/components/weight-input';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import Global from '../../../../../../store/global-selectors';
import type { LtlHandlingUnit, LtlCommodity } from '@shipstation/client-common/types/ltl';
import { FREIGHT_CLASS_OPTIONS, PACKAGING_TYPE_OPTIONS } from '../../../../constants';
import { convertFromWeightUnitEnum } from '../../../../../../services/unit-services/weights/weight-unit-enum-converter';
import { convertFromDimensionUnitEnum } from '../../../../../../services/unit-services/dimensions/dimension-unit-enum-converter';
import type { DimensionUnit } from '../../../../../../prop-types/dimenstion-unit';
import { useFreightQuote, createEmptyCommodity, createEmptyHandlingUnit } from '../../freight-quote-context';

import style from './handling-units-step.module.less';

const handlingUnitsStepMessages = defineMessages({
  continueButton: {
    id: '8la3gr',
    description: 'Continue button in handling units step that triggers quote fetch',
    defaultMessage: 'Continue',
  },
  addHandlingUnit: {
    id: 'R6Mixx',
    description: 'Add Handling Unit button label',
    defaultMessage: '+ Add Handling Unit',
  },
  removeHandlingUnit: {
    id: 'k5vcLR',
    description: 'Remove handling unit button label',
    defaultMessage: 'Remove',
  },
  handlingUnitTitle: {
    id: 'vRk7Mc',
    description: 'Handling unit card title with index',
    defaultMessage: 'Handling Unit {index}',
  },
  packaging: {
    id: 'nuzg6R',
    description: 'Packaging type field label',
    defaultMessage: 'Packaging',
  },
  dimensions: {
    id: 'SpjQYl',
    description: 'Dimensions field label',
    defaultMessage: 'Dimensions',
  },
  addCommodity: {
    id: 'BIa6sj',
    description: 'Add commodity button label',
    defaultMessage: '+ Add Commodity',
  },
  removeCommodity: {
    id: 'EaEs4O',
    description: 'Remove commodity button label',
    defaultMessage: 'Remove',
  },
  commodityTitle: {
    id: '4t5e55',
    description: 'Commodity card title with index',
    defaultMessage: 'Commodity {index}',
  },
  freightClass: {
    id: 'SBc4G4',
    description: 'Freight class field label',
    defaultMessage: 'Class',
  },
  weight: {
    id: 'Wkh5vj',
    description: 'Weight field label',
    defaultMessage: 'Weight',
  },
  collapsedSummary: {
    id: 'Fl9yii',
    description: 'Collapsed handling units step summary',
    defaultMessage: '{count} handling unit(s), {weight} lbs total',
  },
});

type HandlingUnitsStepProps = {
  onContinue: () => void;
};

export const HandlingUnitsStepSummary = () => {
  const { state } = useFreightQuote();
  const intl = useIntl();
  const totalWeight = useMemo(
    () => state.handlingUnits.reduce((sum, hu) => sum + (hu.commodities?.reduce((cSum, c) => cSum + (c.weight ?? 0), 0) ?? 0), 0),
    [state.handlingUnits],
  );

  return (
    <Text
      textType={TextType.Caption}
      value={intl.formatMessage(handlingUnitsStepMessages.collapsedSummary, {
        count: state.handlingUnits.length,
        weight: totalWeight,
      })}
    />
  );
};

export const HandlingUnitsStep = ({ onContinue }: HandlingUnitsStepProps) => {
  const intl = useIntl();
  const { state, localDispatch } = useFreightQuote();
  const handlingUnits = state.handlingUnits;

  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const weightInputUnit = convertFromWeightUnitEnum(sellerDefaultWeightUnit);
  const dimensionInputUnit = convertFromDimensionUnitEnum(sellerDefaultDimensionUnit) as DimensionUnit;

  const handleAddHandlingUnit = useCallback(() => {
    localDispatch({ type: 'ADD_HANDLING_UNIT', handlingUnit: createEmptyHandlingUnit() });
  }, [localDispatch]);

  const handleRemoveHandlingUnit = useCallback(
    (handlingUnitId: string) => {
      localDispatch({ type: 'REMOVE_HANDLING_UNIT', handlingUnitId });
    },
    [localDispatch],
  );

  const handleHandlingUnitFieldChange = useCallback(
    (unitId: string, field: keyof LtlHandlingUnit, value: string | number | boolean) => {
      const updatedUnits = handlingUnits.map((unit) => (unit.id === unitId ? { ...unit, [field]: value } : unit));
      localDispatch({ type: 'UPDATE_HANDLING_UNITS', handlingUnits: updatedUnits });
    },
    [localDispatch, handlingUnits],
  );

  const handleHandlingUnitDimensionsChange = useCallback(
    (unitId: string, dimensions: { length?: number; width?: number; height?: number }) => {
      const updatedUnits = handlingUnits.map((unit) =>
        unit.id === unitId
          ? {
              ...unit,
              length: dimensions.length ?? unit.length,
              width: dimensions.width ?? unit.width,
              height: dimensions.height ?? unit.height,
            }
          : unit,
      );
      localDispatch({ type: 'UPDATE_HANDLING_UNITS', handlingUnits: updatedUnits });
    },
    [localDispatch, handlingUnits],
  );

  const handleAddCommodity = useCallback(
    (unitId: string) => {
      localDispatch({ type: 'ADD_COMMODITY', handlingUnitId: unitId, commodity: createEmptyCommodity() });
    },
    [localDispatch],
  );

  const handleRemoveCommodity = useCallback(
    (unitId: string, commodityId: string) => {
      localDispatch({ type: 'REMOVE_COMMODITY', handlingUnitId: unitId, commodityId });
    },
    [localDispatch],
  );

  const handleCommodityFieldChange = useCallback(
    (unitId: string, commodity: LtlCommodity, field: keyof LtlCommodity, value: string | number | boolean) => {
      localDispatch({
        type: 'UPDATE_COMMODITY',
        handlingUnitId: unitId,
        commodityId: commodity.id,
        commodity: { ...commodity, [field]: value },
      });
    },
    [localDispatch],
  );

  const renderCommodity = (unitId: string, commodity: LtlCommodity, commodityIndex: number, canRemove: boolean) => (
    <div className={style['commodity-card']} key={commodity.id}>
      <FlexRow className={style['commodity-header']}>
        <Text
          textType={TextType.Label}
          value={intl.formatMessage(handlingUnitsStepMessages.commodityTitle, { index: commodityIndex + 1 })}
        />
        {canRemove && (
          <Button onClick={() => handleRemoveCommodity(unitId, commodity.id)} theme={THEME_CONSTANTS.link}>
            <Text textType={TextType.Caption} value={<FormattedMessage {...handlingUnitsStepMessages.removeCommodity} />} />
          </Button>
        )}
      </FlexRow>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...handlingUnitsStepMessages.freightClass} />} />
        <DropdownField
          onItemSelected={(value: string) => handleCommodityFieldChange(unitId, commodity, 'freightClass', value)}
          options={FREIGHT_CLASS_OPTIONS}
          selectedItemValue={commodity.freightClass}
        />
      </div>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...handlingUnitsStepMessages.weight} />} />
        <WeightInput
          onChange={(weightValue: number) => handleCommodityFieldChange(unitId, commodity, 'weight', weightValue)}
          unit={weightInputUnit}
          weight={commodity.weight}
        />
      </div>
    </div>
  );

  const renderHandlingUnit = (unit: LtlHandlingUnit, unitIndex: number) => (
    <div className={style['handling-unit-card']} key={unit.id}>
      <FlexRow className={style['handling-unit-header']}>
        <Text textType={TextType.Label} value={intl.formatMessage(handlingUnitsStepMessages.handlingUnitTitle, { index: unitIndex + 1 })} />
        {handlingUnits.length > 1 && (
          <Button onClick={() => handleRemoveHandlingUnit(unit.id)} theme={THEME_CONSTANTS.link}>
            <Text textType={TextType.Caption} value={<FormattedMessage {...handlingUnitsStepMessages.removeHandlingUnit} />} />
          </Button>
        )}
      </FlexRow>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...handlingUnitsStepMessages.packaging} />} />
        <DropdownField
          onItemSelected={(value: string) => handleHandlingUnitFieldChange(unit.id, 'packagingType', value)}
          options={PACKAGING_TYPE_OPTIONS}
          selectedItemValue={unit.packagingType}
        />
      </div>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...handlingUnitsStepMessages.dimensions} />} />
        <DimensionInput
          className={style['dimension-input-compact']}
          dimensions={{ length: unit.length, width: unit.width, height: unit.height, unit: dimensionInputUnit }}
          onChange={(dimensions: { length?: number; width?: number; height?: number }) =>
            handleHandlingUnitDimensionsChange(unit.id, dimensions)
          }
          unit={dimensionInputUnit}
        />
      </div>
      <FlexColumn className={style['commodities-section']} gap={Gap.Space8}>
        {unit.commodities.map((commodity, commodityIndex) =>
          renderCommodity(unit.id, commodity, commodityIndex, unit.commodities.length > 1),
        )}
        <Button onClick={() => handleAddCommodity(unit.id)} theme={THEME_CONSTANTS.link}>
          <Text textType={TextType.Caption} value={<FormattedMessage {...handlingUnitsStepMessages.addCommodity} />} />
        </Button>
      </FlexColumn>
    </div>
  );

  return (
    <FlexColumn gap={Gap.Space8}>
      {handlingUnits.map((unit, index) => renderHandlingUnit(unit, index))}
      <Button onClick={handleAddHandlingUnit} theme={THEME_CONSTANTS.link}>
        <Text textType={TextType.Caption} value={<FormattedMessage {...handlingUnitsStepMessages.addHandlingUnit} />} />
      </Button>
      <div className={style['step-actions']}>
        <Button onClick={onContinue} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...handlingUnitsStepMessages.continueButton} />
        </Button>
      </div>
    </FlexColumn>
  );
};
